import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaTimes,
  FaImage,
  FaCamera,
  FaMagic,
  FaMicrophone,
  FaMusic
} from "react-icons/fa";
import { FiArrowLeft } from "react-icons/fi";
// Supabase
import { supabase } from "../../supabase/supabase";
import { useLanguage } from "../../contexts/LanguageContext";
import CategoryIcons from "../../components/CategoryIcons/CategoryIcons";
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import "./Create.css";

// Audio helpers: convert Blob/File to 16‑bit PCM WAV for maximum compatibility
async function convertBlobToWavFile(blob) {
  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    const wavBuffer = encodeWavPCM16(audioBuffer);
    const wavBlob = new Blob([wavBuffer], { type: "audio/wav" });
    const fileName = `audio_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.wav`;
    return new File([wavBlob], fileName, { type: "audio/wav" });
  } catch {
    const fallbackName = `audio_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.wav`;
    return new File([blob], fallbackName, { type: "audio/wav" });
  }
}

function encodeWavPCM16(audioBuffer) {
  const numOfChan = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const samples = audioBuffer.length;
  const bytesPerSample = 2;
  const blockAlign = numOfChan * bytesPerSample;
  const buffer = new ArrayBuffer(44 + samples * blockAlign);
  const view = new DataView(buffer);

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + samples * blockAlign, true);
  writeString(view, 8, "WAVE");

  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numOfChan, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);

  writeString(view, 36, "data");
  view.setUint32(40, samples * blockAlign, true);

  const channels = [];
  for (let i = 0; i < numOfChan; i++) channels.push(audioBuffer.getChannelData(i));
  let offset = 44;
  for (let i = 0; i < samples; i++) {
    for (let c = 0; c < numOfChan; c++) {
      let sample = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }
  return buffer;
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i++) view.setUint8(offset + i, string.charCodeAt(i));
}

// Helper function to get thumbnail URL for a link
// Returns the URL directly for YouTube, or null to trigger async fetch for other platforms
const getLinkThumbnailUrl = (url) => {
  if (!url) return "/default-link-thumb.png";

  const lowerUrl = url.toLowerCase();

  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    let videoId = null;

    // YouTube URL patterns - can be handled client-side (direct image URLs)
    if (host === "youtube.com" || host === "m.youtube.com" || host.endsWith("youtube.com")) {
      // YouTube Shorts (youtube.com/shorts/VIDEO_ID)
      const shortsMatch = u.pathname.match(/^\/shorts\/([^\/\?]+)/);
      if (shortsMatch) {
        videoId = shortsMatch[1];
      }
      // YouTube Live (youtube.com/live/VIDEO_ID)
      else if (u.pathname.startsWith('/live/')) {
        const liveMatch = u.pathname.match(/^\/live\/([^\/\?]+)/);
        if (liveMatch) {
          videoId = liveMatch[1];
        }
      }
      // YouTube Embed (youtube.com/embed/VIDEO_ID)
      else if (u.pathname.startsWith('/embed/')) {
        const embedMatch = u.pathname.match(/^\/embed\/([^\/\?]+)/);
        if (embedMatch) {
          videoId = embedMatch[1];
        }
      }
      // Regular YouTube video (?v=VIDEO_ID)
      else {
        videoId = u.searchParams.get("v");
      }
    }
    // Short youtu.be links (youtu.be/VIDEO_ID)
    else if (host === "youtu.be") {
      videoId = u.pathname.slice(1).split('?')[0]; // Remove query params if any
    }

    if (videoId) {
      // Clean video ID (remove any query params or fragments)
      videoId = videoId.split('?')[0].split('#')[0].trim();

      // Use maxresdefault for best quality
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
  } catch (err) {
    console.warn('[Create] Error parsing URL:', err);
  }

  // For all other social media platforms, return null to trigger async fetch from backend
  // This ensures we get the actual thumbnail from oEmbed APIs

  // Instagram
  if (lowerUrl.includes('instagram.com')) {
    return null;
  }

  // TikTok (all URL patterns)
  if (lowerUrl.includes('tiktok.com')) {
    return null;
  }

  // Facebook (all URL patterns)
  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.com') || lowerUrl.includes('fb.watch')) {
    return null;
  }

  // Twitter/X
  if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) {
    return null;
  }

  // Snapchat
  if (lowerUrl.includes('snapchat.com') || lowerUrl.includes('snap.com')) {
    return null;
  }

  // Pinterest
  if (lowerUrl.includes('pinterest.com') || lowerUrl.includes('pin.it')) {
    return null;
  }

  // LinkedIn
  if (lowerUrl.includes('linkedin.com')) {
    return null;
  }

  // Vimeo
  if (lowerUrl.includes('vimeo.com')) {
    return null;
  }

  // For unknown URLs, still try to fetch via backend (OG scraping)
  return null;
};

// Backend API URL - adjust if your backend runs on a different port/domain
const BACKEND_API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Unified function to fetch link thumbnail via backend (avoids CORS)
const fetchLinkThumbnail = async (url) => {
  try {
    console.log('[Create] Fetching thumbnail for:', url);
    const backendUrl = `${BACKEND_API_URL}/api/fetch-link-thumbnail?url=${encodeURIComponent(url)}`;
    console.log('[Create] Backend URL:', backendUrl);

    const response = await fetch(backendUrl);

    console.log('[Create] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Create] Backend error response:', errorText);
      throw new Error(`Failed to fetch thumbnail: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[Create] Backend response data:', data);

    // Return thumbnail URL if available, otherwise return default
    if (data.thumbnail_url) {
      console.log('[Create] Using thumbnail URL:', data.thumbnail_url);
      return data.thumbnail_url;
    }

    // Return default thumbnail based on link type from backend response
    console.log('[Create] No thumbnail URL, using default:', data.default);
    return data.default || getDefaultThumbnailForUrl(url);
  } catch (error) {
    console.error('[Create] Thumbnail fetch error:', error);
    console.error('[Create] Error details:', error.message, error.stack);

    // Fallback: determine default based on URL
    return getDefaultThumbnailForUrl(url);
  }
};

// Helper function to get platform-specific default thumbnail
const getDefaultThumbnailForUrl = (url) => {
  const lowerUrl = url.toLowerCase();

  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.com') || lowerUrl.includes('fb.watch')) {
    return "/default-facebook-thumb2.png";
  } else if (lowerUrl.includes('instagram.com')) {
    return "/default-instagram-thumb.jpeg";
  } else if (lowerUrl.includes('tiktok.com')) {
    return "/default-tiktok-thumb.jpeg";
  } else if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    return "/default-youtubeshorts-thumb.jpeg";
  } else if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) {
    return "/default-link-placeholder.png";
  } else if (lowerUrl.includes('snapchat.com') || lowerUrl.includes('snap.com')) {
    return "/default-link-placeholder.png";
  } else if (lowerUrl.includes('pinterest.com') || lowerUrl.includes('pin.it')) {
    return "/default-link-placeholder.png";
  } else if (lowerUrl.includes('linkedin.com')) {
    return "/default-link-placeholder.png";
  } else if (lowerUrl.includes('vimeo.com')) {
    return "/default-link-placeholder.png";
  }

  return "/default-link-placeholder.png";
};

// Keep these for backward compatibility, but they now use the unified function
const fetchInstagramThumbnail = (url) => fetchLinkThumbnail(url);
const fetchTikTokThumbnail = (url) => fetchLinkThumbnail(url);
const fetchFacebookThumbnail = (url) => fetchLinkThumbnail(url);

// Helper function to save thumbnail from URL to storage
const saveThumbnailFromUrl = async (thumbnailUrl, userId, postId) => {
  try {
    if (!thumbnailUrl || thumbnailUrl.startsWith("/default-")) {
      return; // Skip default placeholders
    }

    const response = await fetch(thumbnailUrl);
    if (!response.ok) {
      console.warn("Failed to fetch thumbnail from URL:", thumbnailUrl);
      return;
    }

    const blob = await response.blob();
    const thumbnailFile = new File([blob], `thumbnail.jpg`, { type: "image/jpeg" });

    // Save thumbnail to storage: {userId}/{postId}_thumb.jpg
    const thumbnailPath = `${userId}/${postId}_thumb.jpg`;
    const { error: thumbError } = await supabase.storage
      .from("post-media")
      .upload(thumbnailPath, thumbnailFile, {
        cacheControl: "3600",
        upsert: true, // Allow overwrite if exists
        contentType: "image/jpeg",
      });

    if (thumbError) {
      console.warn("Failed to save thumbnail to storage:", thumbError);
    } else {
      console.log("Thumbnail saved successfully:", thumbnailPath);
    }
  } catch (err) {
    console.warn("Error saving thumbnail from URL:", err);
  }
};

// Helper function to resolve avatar URL
const resolveAvatarUrl = (filename, userFolderId) => {
  if (!filename) return '/default-avatar.png';

  // if already a full url, return it
  if (typeof filename === 'string' && /^(http|https):\/\//i.test(filename)) {
    return filename;
  }
  // if data url, return it
  if (typeof filename === 'string' && filename.startsWith('data:')) return filename;

  // Construct the proper path: avatars/<userId>/<filename>
  const path = `${userFolderId}/${filename}`;

  try {
    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    if (data?.publicUrl) {
      return data.publicUrl;
    }
  } catch (e) {
    console.log('[Create] Error resolving avatar URL:', e);
  }

  return '/default-avatar.png';
};

export default function Create() {
  const [currentUser, setCurrentUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [linkToCategory, setLinkToCategory] = useState(false);
  const [addCustomLink, setAddCustomLink] = useState(false);
  const [allowSponsors, setAllowSponsors] = useState(false);
  const [media, setMedia] = useState([]);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [placeholderVisible, setPlaceholderVisible] = useState(true);
  const [textContent, setTextContent] = useState("");
  const [linkUrl, setLinkUrl] = useState(null);
  const [linkThumbnail, setLinkThumbnail] = useState(null);
  const [linkThumbnailLoading, setLinkThumbnailLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [customLink, setCustomLink] = useState("");
  const [customLinkLabel, setCustomLinkLabel] = useState("");
  const [durationStep, setDurationStep] = useState(0);
  const [targetUSD, setTargetUSD] = useState(0);
  const [winis, setWinis] = useState(1);

  const navigate = useNavigate();
  const location = useLocation();
  const [canLockPosts, setCanLockPosts] = useState(false);

  const inputRef = useRef(null);
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const musicInputRef = useRef(null);
  const { translations: t } = useLanguage();
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [privatePost, setPrivatePost] = useState(false);
  const [lockPost, setLockPost] = useState(false);
  const [landscapeMode, setLandscapeMode] = useState(false);
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState({
    code: "USD",
    name: "US Dollar",

  });
  const [winisToUSD, setWinisToUSD] = useState(12);
  const [currencyInput, setCurrencyInput] = useState(
    Math.round(winis * winisToUSD * selectedCurrency.rate).toString()
  );
  // Supabase authentication
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.log('[Create auth] getSession error', error);
        }
        let user = data?.session?.user || null;

        // Fallback: check localStorage if no session
        if (!user) {
          const storedUser = localStorage.getItem('supabase_user');
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              console.log('[Create auth] using stored user from localStorage', parsedUser);
              user = parsedUser;
            } catch (e) {
              console.log('[Create auth] failed to parse stored user', e);
              localStorage.removeItem('supabase_user');
            }
          }
        }

        console.log('[Create auth] init session', { hasSession: !!data?.session, userId: user?.id, email: user?.email });

        if (!mounted) return;

        if (user) {
          setUserId(user.id);
          // Fetch complete user profile from Supabase
          try {
            const { data: profile, error } = await supabase
              .from('profiles')
              .select('display_name, username, avatar_url, subscription_cost, subscription_duration')
              .eq('user_id', user.id)
              .single();

            if (error || !profile) {
              console.log('[Create auth] No profile found, using fallback');
              setCurrentUser({
                username: user.user_metadata?.full_name || user.user_metadata?.name || 'User',
                profileImage: user.user_metadata?.avatar_url || user.user_metadata?.picture || "/default-avatar.png"
              });
            } else {
              // Use Supabase profile data
              const avatarUrl = resolveAvatarUrl(profile.avatar_url, user.id);
              setCurrentUser({
                username: profile.username || 'User',
                profileImage: avatarUrl
              });
              const hasSubscription =
                !!profile.subscription_cost && !!profile.subscription_duration;
              setCanLockPosts(hasSubscription);
            }
          } catch (err) {
            console.error('[Create auth] Error fetching profile:', err);
            setCurrentUser({
              username: user.user_metadata?.full_name || user.user_metadata?.name || 'User',
              profileImage: user.user_metadata?.avatar_url || user.user_metadata?.picture || "/default-avatar.png"
            });
          }
        } else {
          setCurrentUser(null);
        }
      } catch (err) {
        console.error('[Create auth] init threw', err);
      }
    };

    const { data: listener } = supabase.auth.onAuthStateChange(async (event, session) => {
      const user = session?.user || null;
      console.log('[Create auth] state change', { event, userId: user?.id, email: user?.email });

      if (!mounted) return;

      if (user) {
        setUserId(user.id);
        // Store user session in localStorage for persistence
        localStorage.setItem('supabase_user', JSON.stringify({
          id: user.id,
          email: user.email,
          user_metadata: user.user_metadata
        }));

        // Fetch complete user profile from Supabase
        try {
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('display_name, username, avatar_url, subscription_cost, subscription_duration')
            .eq('user_id', user.id)
            .single();

          if (error || !profile) {
            console.log('[Create auth] No profile found, using fallback');
            setCurrentUser({
              username: user.user_metadata?.full_name || user.user_metadata?.name || 'User',
              profileImage: user.user_metadata?.avatar_url || user.user_metadata?.picture || "/default-avatar.png"
            });
          } else {
            // Use Supabase profile data
            const avatarUrl = resolveAvatarUrl(profile.avatar_url, user.id);
            setCurrentUser({
              username: profile.username || 'User',
              profileImage: avatarUrl
            });
            const hasSubscription =
              !!profile.subscription_cost && !!profile.subscription_duration;
            setCanLockPosts(hasSubscription);
          }
        } catch (err) {
          console.error('[Create auth] Error fetching profile:', err);
          setCurrentUser({
            username: user.user_metadata?.full_name || user.user_metadata?.name || 'User',
            profileImage: user.user_metadata?.avatar_url || user.user_metadata?.picture || "/default-avatar.png"
          });
        }
      } else {
        // Clear localStorage when user signs out
        localStorage.removeItem('supabase_user');
        setCurrentUser(null);
        setUserId(null);
      }
    });

    init();

    return () => {
      mounted = false;
      try {
        listener?.subscription?.unsubscribe && listener.subscription.unsubscribe();
      } catch (e) {
        // ignore
      }
    };
  }, []);

  // Check if lockPost query parameter is set and user has subscription
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const lockPostParam = searchParams.get('lockPost');

    if (lockPostParam === 'true' && canLockPosts) {
      // Enable advanced options and lock post
      setShowAdvancedOptions(true);
      setLockPost(true);
    }
  }, [location.search, canLockPosts]);

  const handleClose = () => navigate(-1);
  const handleUpload = async () => {
    if (uploading) return;
    if (!userId) {
      alert("Please sign in to upload.");
      return;
    }

    // Build caption: include user text and link URL if present
    let captionStr = (textContent || "").trim();

    // If we have a detected linkUrl that's not already in the caption, add it
    if (linkUrl) {
      const URL_REGEX = /https?:\/\/[^\s]+/g;
      const hasLinkInCaption = captionStr.match(URL_REGEX);

      // If link is not in caption, append it
      if (!hasLinkInCaption || !hasLinkInCaption.includes(linkUrl)) {
        // Add link to caption with proper spacing
        if (captionStr.length > 0) {
          captionStr = `${captionStr} ${linkUrl}`;
        } else {
          captionStr = linkUrl;
        }
      }
    }

    const itemsWithFiles = media.filter(m => m?.file instanceof Blob || m?.file instanceof File);

    // Validate: need at least caption, media, or link
    if (itemsWithFiles.length === 0 && captionStr.length === 0) {
      alert("Add a photo, video, voice note, link, or caption before uploading.");
      return;
    }
    setUploading(true);
    try {
      const prefix = Date.now().toString();
      let mediaIndex = 0;
      const uploadedNames = [];
      let uploadedAudioName = null;

      for (const item of itemsWithFiles) {
        let file = item.file;
        const original = file.name || "";
        const rawExtFromName = original.includes(".") ? original.split(".").pop().toLowerCase() : "";
        const rawExtFromType = (file.type && file.type.split("/")[1]) || "";
        let ext = rawExtFromName || rawExtFromType || "bin";
        if (ext === "jpeg") ext = "jpg";
        if (ext === "quicktime") ext = "mov";
        if (ext === "x-m4a") ext = "m4a";

        // Ensure audio is stored as .wav
        if (item.type === "audio" && file.type !== "audio/wav") {
          file = await convertBlobToWavFile(file);
          ext = "wav";
        }

        const indexForThis = item.type === "audio" ? mediaIndex : mediaIndex;
        const name = `${prefix}_${indexForThis}.${item.type === "audio" ? "wav" : ext}`;
        const path = `${userId}/${name}`;
        const { error } = await supabase.storage.from("post-media").upload(path, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: file.type || undefined,
        });
        if (error) throw error;

        if (item.type === "audio") {
          uploadedAudioName = name;
        } else {
          uploadedNames.push(name);
          mediaIndex += 1;
        }
      }

      // Build the post data object
      const postData = {
        user_id: userId,
        caption: captionStr || "",
        media_names: uploadedNames.length > 0 ? uploadedNames : [],
        audio_filename: uploadedAudioName || null,
        is_private: privatePost,
        is_locked: lockPost && canLockPosts,
      };

      console.log("[Create] Inserting post with data:", postData);

      const { data: insertedPost, error: insertError } = await supabase
        .from("posts")
        .insert(postData)
        .select()
        .single();

      if (insertError) {
        console.error("[Create] Insert error:", insertError);
        throw insertError;
      }

      console.log("[Create] Post inserted successfully:", insertedPost);

      // If there's a link, fetch and save the thumbnail
      if (linkUrl && insertedPost?.id) {
        try {
          let thumbnailUrl = getLinkThumbnailUrl(linkUrl);

          // For Instagram/TikTok/Facebook, fetch thumbnail asynchronously
          if (thumbnailUrl === null) {
            const url = linkUrl.toLowerCase();
            if (url.includes('instagram.com')) {
              thumbnailUrl = await fetchInstagramThumbnail(linkUrl);
            } else if (url.includes('tiktok.com')) {
              thumbnailUrl = await fetchTikTokThumbnail(linkUrl);
            } else if (url.includes('facebook.com')) {
              thumbnailUrl = await fetchFacebookThumbnail(linkUrl);
            }
          }

          // Save thumbnail if we have a valid URL
          if (thumbnailUrl && !thumbnailUrl.startsWith("/default-")) {
            await saveThumbnailFromUrl(thumbnailUrl, userId, insertedPost.id);
          }
        } catch (thumbErr) {
          console.warn("Error saving thumbnail:", thumbErr);
          // Don't throw - post is already created, thumbnail is optional
        }
      }

      // Reset UI
      setMedia([]);
      setTextContent("");
      setLinkUrl(null);
      setLinkThumbnail(null);
      if (inputRef.current) inputRef.current.textContent = "";
      alert("Post uploaded!");
    } catch (err) {
      console.error("[Create] Upload failed:", err);
      alert("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleInput = () => {
    const content = inputRef.current.textContent;
    const isEmpty = content.trim() === "";
    setPlaceholderVisible(isEmpty);
    setTextContent(content);
  };

  // Detect and extract link from text content
  useEffect(() => {
    const URL_REGEX = /https?:\/\/[^\s]+/g;
    const matches = textContent.match(URL_REGEX);

    if (matches && matches.length > 0) {
      const firstUrl = matches[0];
      if (firstUrl !== linkUrl) {
        setLinkUrl(firstUrl);
        setLinkThumbnail(null); // Reset thumbnail for new link

        const thumbUrl = getLinkThumbnailUrl(firstUrl);
        if (thumbUrl === null) {
          // Need to fetch thumbnail asynchronously from backend
          setLinkThumbnailLoading(true);
          fetchLinkThumbnail(firstUrl)
            .then(thumb => {
              setLinkThumbnail(thumb);
              setLinkThumbnailLoading(false);
            })
            .catch(() => {
              // Use platform-specific default on error
              setLinkThumbnail(getDefaultThumbnailForUrl(firstUrl));
              setLinkThumbnailLoading(false);
            });
        } else {
          // YouTube or other direct thumbnail URLs
          setLinkThumbnail(thumbUrl);
        }
      }
    } else {
      if (linkUrl) {
        setLinkUrl(null);
        setLinkThumbnail(null);
        setLinkThumbnailLoading(false);
      }
    }
  }, [textContent, linkUrl]);

  const handlePhotoClick = () => photoInputRef.current.click();
  const handleMusicClick = () => musicInputRef.current.click();
  const handleCameraClick = () => cameraInputRef.current.click();

  const handleMediaChange = (e, type) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const created = files.map((file) => {
      const detected =
        file.type?.startsWith("video/") ? "video" :
          file.type?.startsWith("image/") ? "image" :
            type;
      return {
        url: URL.createObjectURL(file),
        type: detected,
        file,
      };
    });
    setMedia(prev => [...prev, ...created]);
    // clear input to allow re-selecting same files
    e.target.value = "";
  };

  // Timer effect for recording duration
  useEffect(() => {
    let interval = null;
    if (recording) {
      setRecordingDuration(0);
      interval = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } else {
      setRecordingDuration(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [recording]);

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVoiceClick = async () => {
    const getSupportedMimeType = () => {
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
      ];
      for (const t of candidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(t)) return t;
      }
      return "";
    };

    if (!recording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: 48000,
          },
        });
        const mimeType = getSupportedMimeType();
        const recorder = new MediaRecorder(stream, {
          ...(mimeType ? { mimeType } : {}),
          audioBitsPerSecond: 128000,
        });
        const chunks = [];
        recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
        recorder.onstop = async () => {
          try {
            const blob = new Blob(chunks, { type: mimeType || "audio/webm" });
            const wavFile = await convertBlobToWavFile(blob);
            const url = URL.createObjectURL(wavFile);
            setMedia(prev => [...prev, { url, type: "audio", file: wavFile }]);
          } finally {
            try { stream.getTracks().forEach(t => t.stop()); } catch { }
            setMediaStream(null);
          }
        };
        recorder.start(250);
        setMediaRecorder(recorder);
        setMediaStream(stream);
        setRecording(true);
      } catch {
        alert("Microphone access denied.");
      }
    } else {
      try { mediaRecorder && mediaRecorder.stop(); } catch { }
      try { mediaStream && mediaStream.getTracks().forEach(t => t.stop()); } catch { }
      setRecording(false);
    }
  };
  //fetch currencies from Supabase
  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('[Create] fetching currency data from Supabase...');

        // Fetch winicoin exchange rate
        const { data: winiData, error: winiError } = await supabase
          .from('app_settings')
          .select('winicoinUsdExchangeRate')
          .eq('key', 'winicoins')
          .single();

        if (winiError) {
          console.error('[Create] Supabase wini rate error:', winiError);
        } else if (winiData?.winicoinUsdExchangeRate) {
          setWinisToUSD(winiData.winicoinUsdExchangeRate);
        }

        // Fetch currencies
        const { data: currencyData, error: currencyError } = await supabase
          .from('currencies')
          .select('iso, name, rate')
          .not('rate', 'is', null)
          .not('name', 'is', null);

        if (currencyError) {
          console.error('[Create] Supabase currency error:', currencyError);
        } else if (currencyData) {
          const currencyList = currencyData.map(currency => ({
            code: currency.iso,
            name: currency.name,
            rate: currency.rate,
          }));

          setCurrencies(currencyList);
          if (currencyList.length > 0) {
            const defaultCurrency = currencyList.find(c => c.code === "USD") || currencyList[0];
            setSelectedCurrency(defaultCurrency);
          }
        }
      } catch (error) {
        console.error("[Create] Error fetching data:", error);
      }
    };

    fetchData();
  }, []);



  // Update currencyValue when winisToUSD or selectedCurrency changes
  useEffect(() => {
    setCurrencyInput(Math.round(winis * winisToUSD * selectedCurrency.rate).toString());
  }, [winis, winisToUSD, selectedCurrency]);

  const removeMedia = index => setMedia(prev => prev.filter((_, i) => i !== index));

  const openLink = (url) => {
    // Try to open in app on mobile, fallback to browser
    try {
      const urlObj = new URL(url);
      const host = urlObj.hostname.replace(/^www\./, "");

      // YouTube - try to open in app
      if (host === "youtu.be" || host.includes("youtube.com")) {
        const videoId = host === "youtu.be"
          ? urlObj.pathname.slice(1)
          : urlObj.searchParams.get("v") || urlObj.pathname.match(/\/shorts\/([^\/\?]+)/)?.[1];
        if (videoId) {
          // Try YouTube app deep link
          const youtubeAppUrl = `vnd.youtube:${videoId}`;
          const youtubeWebUrl = `https://www.youtube.com/watch?v=${videoId}`;
          window.location.href = youtubeAppUrl;
          // Fallback to web if app doesn't open
          setTimeout(() => {
            window.open(youtubeWebUrl, "_blank", "noopener,noreferrer");
          }, 500);
          return;
        }
      }

      // Instagram - try to open in app
      if (host.includes("instagram.com")) {
        const instagramAppUrl = url.replace("https://", "instagram://");
        window.location.href = instagramAppUrl;
        setTimeout(() => {
          window.open(url, "_blank", "noopener,noreferrer");
        }, 500);
        return;
      }

      // TikTok - try to open in app
      if (host.includes("tiktok.com")) {
        const tiktokAppUrl = url.replace("https://", "snssdk1233://");
        window.location.href = tiktokAppUrl;
        setTimeout(() => {
          window.open(url, "_blank", "noopener,noreferrer");
        }, 500);
        return;
      }

      // Default: open in browser
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      // Fallback if URL parsing fails
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const removeLink = () => {
    setLinkUrl(null);
    setLinkThumbnail(null);
    setLinkThumbnailLoading(false);

    // Remove link from text content
    const URL_REGEX = /https?:\/\/[^\s]+/g;
    const cleanedText = textContent.replace(URL_REGEX, "").trim();
    setTextContent(cleanedText);
    if (inputRef.current) {
      inputRef.current.textContent = cleanedText;
    }
    setPlaceholderVisible(cleanedText === "");
  };

  const durations = ["1 day", "5 days", "10 days", "15 days", "20 days", "25 days", "30 days"];
  const duration = durations[durationStep];

  return (
    <div className="create-page">
      <FloatingSidebarButton userProfile={currentUser} />
      {/* <button
        className="pages-back-btn"
        onClick={handleClose}
        aria-label="Back"
        title="Back"
      >
        <FiArrowLeft />
      </button> */}
      <header className="create-header-page">
        <button className="close-btn" onClick={handleClose}><FaTimes /></button>
        <button className="upload-btn" onClick={handleUpload} disabled={uploading}>
          {uploading ? (t.uploading || "Uploading...") : (t.upload || "Upload")}
        </button>
      </header>

      <div className="create-body">
        <div className="user-row">
          <img
            src={currentUser?.profileImage || "/default-avatar.png"}
            alt="avatar"
            className="create-avatar"
          />
          <span className="create-username">@{currentUser?.username}</span>

        </div>

        <div className="create-input-wrapper">
          {placeholderVisible && (
            <div className="placeholder-text">{t.whatsOnYourMind || "What's on your mind?"}</div>
          )}
          <div
            className="create-input"
            ref={inputRef}
            contentEditable
            onInput={handleInput}
            suppressContentEditableWarning={true}
          />
        </div>

        <div className="media-preview">
          {media.map((item, index) => (
            <div key={index} className={`media-card ${item.type === "audio" ? "audio-card" : ""}`}>
              <button className="remove-media" onClick={() => removeMedia(index)}>×</button>
              {item.type === "image" && <img src={item.url} alt="preview" className="media-thumb" />}
              {item.type === "video" && (
                <video className="media-video" controls src={item.url} />
              )}
              {item.type === "audio" && (
                <InlineAudioPlayer src={item.url} index={index} />
              )}
            </div>
          ))}

          {/* Link Preview */}
          {linkUrl && (
            <div className="media-card link-card">
              <button className="remove-media" onClick={removeLink}>×</button>
              {linkThumbnailLoading ? (
                <div className="link-thumb-container link-loading">
                  <div className="link-loading-spinner">
                    <div className="spinner"></div>
                    <span>Loading preview...</span>
                  </div>
                </div>
              ) : linkThumbnail ? (
                <div className="link-thumb-container">
                  <img
                    src={linkThumbnail}
                    alt="Link thumbnail"
                    className="link-thumb"
                    onClick={(e) => {
                      e.stopPropagation();
                      openLink(linkUrl);
                    }}
                    onError={(e) => {
                      // Prevent infinite loop
                      if (e.target.src.includes('default-')) return;

                      // Check link type and use appropriate default thumbnail
                      if (!linkUrl) {
                        e.target.src = "/default-link-thumb.png";
                        return;
                      }

                      const url = linkUrl.toLowerCase();

                      // Try different YouTube thumbnail qualities before falling back to default
                      if (url.includes('youtube.com') || url.includes('youtu.be')) {
                        // Extract video ID and try mqdefault (medium quality) as fallback
                        try {
                          const u = new URL(linkUrl);
                          const host = u.hostname.replace(/^www\./, "");
                          let videoId = null;

                          if (host === "youtube.com" || host === "m.youtube.com" || host.endsWith("youtube.com")) {
                            const shortsMatch = u.pathname.match(/^\/shorts\/([^\/\?]+)/);
                            const liveMatch = u.pathname.match(/^\/live\/([^\/\?]+)/);
                            const embedMatch = u.pathname.match(/^\/embed\/([^\/\?]+)/);

                            if (shortsMatch) {
                              videoId = shortsMatch[1];
                            } else if (liveMatch) {
                              videoId = liveMatch[1];
                            } else if (embedMatch) {
                              videoId = embedMatch[1];
                            } else {
                              videoId = u.searchParams.get("v");
                            }
                          } else if (host === "youtu.be") {
                            videoId = u.pathname.slice(1).split('?')[0];
                          }

                          if (videoId) {
                            videoId = videoId.split('?')[0].split('#')[0].trim();
                            // Try mqdefault (medium quality) as fallback
                            e.target.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                            return;
                          }
                        } catch (err) {
                          // Fall through to default
                        }

                        // If still fails, use YouTube Shorts default
                        if (url.includes('/shorts/') || url.includes('youtube.com/shorts')) {
                          e.target.src = "/default-youtubeshorts-thumb.jpeg";
                        } else {
                          e.target.src = "/default-youtubeshorts-thumb.jpeg";
                        }
                      } else {
                        // For all other platforms, use the helper function
                        e.target.src = getDefaultThumbnailForUrl(linkUrl);
                      }
                    }}
                  />
                </div>
              ) : null}
              <div
                className="link-text-container"
                onClick={(e) => {
                  e.stopPropagation();
                  openLink(linkUrl);
                }}
              >
                <span className="link-text">{linkUrl}</span>
              </div>
            </div>
          )}
        </div>

        <div className="create-media-icons">
          <div className="tooltip-wrapper" title={t.photo || "Photo"}>
            <FaImage className="create-icon" onClick={handlePhotoClick} />
            <input type="file" accept="image/*,video/*" multiple ref={photoInputRef} style={{ display: "none" }} onChange={e => handleMediaChange(e, "image")} />
          </div>

          <div className="tooltip-wrapper" title={t.camera || "Camera"}>
            <FaCamera className="create-icon" onClick={handleCameraClick} />
            <input type="file" accept="image/*" capture="environment" ref={cameraInputRef} style={{ display: "none" }} onChange={e => handleMediaChange(e, "image")} />
          </div>

          {/* <div className="tooltip-wrapper" title={t.aiAssistant || "AI Assistant"}>
            <FaMagic className="create-icon" />
          </div> */}

          <div className="tooltip-wrapper" title={t.voice || "Voice"}>
            <FaMicrophone className={`create-icon ${recording ? "recording" : ""}`} onClick={handleVoiceClick} />
          </div>

          {/* Recording UI Overlay */}
          {recording && (
            <div className="recording-overlay">
              <div className="recording-indicator">
                <div className="recording-dot"></div>
                <span className="recording-timer">{formatRecordingTime(recordingDuration)}</span>
              </div>
              <button
                className="stop-recording-btn"
                onClick={handleVoiceClick}
                aria-label="Stop recording"
              >
                Stop
              </button>
            </div>
          )}

          <div className="tooltip-wrapper" title={t.music || "Music"}>
            <FaMusic className="create-icon" onClick={handleMusicClick} />
            <input type="file" accept="audio/*" ref={musicInputRef} style={{ display: "none" }} onChange={e => handleMediaChange(e, "audio")} />
          </div>
        </div>



        <hr className="divider" />

        <div className="toggle-item">
          <span>{t.linkYourPost || "Link Your Post To Category"}</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={linkToCategory}
              onChange={() => {
                setLinkToCategory(prev => {
                  const newValue = !prev;
                  if (!newValue) setSelectedCategoryId(null);
                  return newValue;
                });
              }}
            />
            <span className="slider" />
          </label>
        </div>

        {linkToCategory && (
          <CategoryIcons
            selectedCategoryId={selectedCategoryId}
            onCategoryClick={setSelectedCategoryId}
          />
        )}

        <div className="toggle-item">
          <span>{t.addCustomLink || "Add Custom Link"}</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={addCustomLink}
              onChange={() => setAddCustomLink(prev => !prev)}
            />
            <span className="slider" />
          </label>
        </div>

        {addCustomLink && (
          <div className="custom-link-fields">
            <input
              type="text"
              className="custom-link-input"
              placeholder={t.customLinkPlaceholder || "Enter your custom Link here"}
              value={customLink}
              onChange={(e) => setCustomLink(e.target.value)}
            />
            <div className="custom-link-label-wrapper">
              <input
                type="text"
                className="custom-link-label"
                placeholder={t.openLink || "Open Link"}
                maxLength={9}
                value={customLinkLabel}
                onChange={(e) => setCustomLinkLabel(e.target.value)}
              />
              <div className="char-counter">{customLinkLabel.length}/9</div>
            </div>
          </div>
        )}

        <div className="toggle-item">
          <span>{t.allowSponsors || "Allow Sponsors"} <small>(T&Cs apply)</small></span>
          <label className="switch">
            <input
              type="checkbox"
              checked={allowSponsors}
              onChange={() => setAllowSponsors(v => !v)}
            />
            <span className="slider" />
          </label>
        </div>

        {allowSponsors && (
          <>
            <div className="sponsor-section">
              <div className="slider-label">{t.duration || "Duration"}: {duration}</div>
              <input
                type="range"
                min={0}
                max={6}
                step={1}
                value={durationStep}
                onChange={(e) => setDurationStep(parseInt(e.target.value))}
                className="duration-slider"
              // style={durationSliderStyle}
              />
            </div>

            <div className="sponsor-section">
              <div className="slider-label">
                {t.targetIn || "Target In"}
                <div className="currency-selector">
                  <button
                    className="currency-button"
                    onClick={() => setShowCurrencyMenu(!showCurrencyMenu)}
                  >
                    {/* <img
                src={selectedCurrency.flag}
                alt={selectedCurrency.code}
                className="currency-flag-img"
              /> */}
                    {selectedCurrency.code}
                    <span className={`dropdown-icon ${showCurrencyMenu ? 'open' : ''}`}>▼</span>
                  </button>
                  {showCurrencyMenu && (
                    <div className="currency-dropdown">
                      {currencies.map(currency => (
                        <div
                          key={currency.code}
                          className="currency-option"
                          onClick={() => {
                            setSelectedCurrency(currency);
                            setShowCurrencyMenu(false);
                          }}
                        >
                          {/* <img
                      src={currency.flag}
                      alt={currency.code}
                      className="currency-flag-img"
                    /> */}
                          <span className="currency-text">
                            {currency.code} - {currency.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <input
                type="range"
                min={0}
                max={1000000}
                step={10000}
                value={targetUSD}
                onChange={(e) => setTargetUSD(parseInt(e.target.value))}
                className="target-slider"
                style={{ '--progress': `${(targetUSD / 1000000) * 100}%` }}
              />
              <div className="target-amount">{targetUSD.toLocaleString()} {selectedCurrency.code}</div>
            </div>

            <div className="sponsor-section">
              <div className="section-title">SPONSOR SEGMENT</div>
              <div className="winis-converter-container">
                <div className="converter-column">
                  <div className="currency-label">Wini Coin</div>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={winis}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10) || 1;
                      setWinis(Math.max(1, value));
                    }}
                    className="wini-coin-input"
                  />
                </div>
                <div className="converter-arrow">⇄</div>
                <div className="converter-column">
                  <div className="currency-label">{selectedCurrency.code}</div>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={currencyInput}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      setCurrencyInput(rawValue); // Store raw input

                      if (rawValue === '') {
                        setWinis(0); // When field is empty, set winis to 0
                      } else {
                        const numericValue = parseFloat(rawValue) || 0;
                        const newWinis = numericValue / (winisToUSD * selectedCurrency.rate);
                        setWinis(Math.max(0, newWinis));
                      }
                    }}
                    onBlur={() => {
                      if (currencyInput === '') {
                        setWinis(1); // Reset to 1 wini when field is empty and loses focus
                        setCurrencyInput(Math.round(1 * winisToUSD * selectedCurrency.rate));
                      }
                    }}
                    className="currency-input"
                  />
                </div>
              </div>
            </div>
          </>
        )}
        <div
          className="toggle-advanced-item"
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
        >
          <span>{t.create?.advancedOptions?.title || "Advanced Options"}</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={showAdvancedOptions}
              onChange={() => setShowAdvancedOptions(v => !v)}
            />
            <span className="slider" />
          </label>
        </div>

        {showAdvancedOptions && (
          <div className="advanced-options">
            <div className="toggle-item">
              <span>{t.create?.advancedOptions?.privatePost || "Private Post"}</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={privatePost}
                  onChange={() => setPrivatePost(!privatePost)}
                />
                <span className="slider" />
              </label>
            </div>

            <div className={`toggle-item ${!canLockPosts ? "lock-disabled" : ""}`}>
              <span>{t.create?.advancedOptions?.lockPost || "Lock Post"}</span>
              <label className={`switch ${!canLockPosts ? "switch-disabled" : ""}`}>
                <input
                  type="checkbox"
                  checked={lockPost && canLockPosts}
                  disabled={!canLockPosts}
                  onChange={() => {
                    if (!canLockPosts) {
                      alert("You need an active subscription to lock posts.");
                      return;
                    }
                    setLockPost(!lockPost);
                  }}
                />
                <span className="slider" />
              </label>
            </div>

            <div className="toggle-item">
              <span>{t.create?.advancedOptions?.landscapeMode || "Landscape Mode"}</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={landscapeMode}
                  onChange={() => setLandscapeMode(!landscapeMode)}
                />
                <span className="slider" />
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InlineAudioPlayer({ src, index }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const formattedTime = (time) => {
    if (!Number.isFinite(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const fixDurationIfNeeded = () => {
      if (!isFinite(audio.duration) || audio.duration === 0) {
        const seekableEnd = (() => {
          try { return audio.seekable?.length ? audio.seekable.end(audio.seekable.length - 1) : null; } catch { return null; }
        })();
        const target = seekableEnd || 1e6;
        const onSeeked = () => {
          try {
            setDuration(audio.duration || 0);
            audio.currentTime = 0;
          } catch { }
          audio.removeEventListener("seeked", onSeeked);
        };
        audio.addEventListener("seeked", onSeeked);
        try { audio.currentTime = target; } catch { }
        return;
      }
      setDuration(audio.duration);
    };

    const handleLoaded = () => { fixDurationIfNeeded(); };
    const handleTimeUpdate = () => {
      if (!audio.duration) return;
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / audio.duration) * 100);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };
    const handlePause = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);

    audio.addEventListener("loadedmetadata", handleLoaded);
    audio.addEventListener("durationchange", handleLoaded);
    audio.addEventListener("loadeddata", handleLoaded);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("play", handlePlay);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoaded);
      audio.removeEventListener("durationchange", handleLoaded);
      audio.removeEventListener("loadeddata", handleLoaded);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("play", handlePlay);
    };
  }, [src]);

  useEffect(() => {
    const pauseOthers = (event) => {
      if (event.detail?.index === index) return;
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
    window.addEventListener("create-page-audio-play", pauseOthers);
    return () => window.removeEventListener("create-page-audio-play", pauseOthers);
  }, [index]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      window.dispatchEvent(new CustomEvent("create-page-audio-play", { detail: { index } }));
      audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const nextProgress = Number(e.target.value);
    const nextTime = (nextProgress / 100) * audio.duration;
    audio.currentTime = nextTime;
    setProgress(nextProgress);
  };

  return (
    <div className="inline-audio-player">
      <audio ref={audioRef} src={src} preload="metadata" />
      <button type="button" className="audio-play-button" onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"}>
        {isPlaying ? "❚❚" : "▶"}
      </button>
      <div className="audio-track">
        <input type="range" min="0" max="100" value={progress} onChange={handleSeek} className="audio-progress" aria-label="Seek voice note" />
        <div className="audio-time">
          <span>{formattedTime(currentTime)}</span>
          <span>{formattedTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}